import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { ReportLogoInfo } from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { firmSettingsService } from '@/core/services/firmSettings';
import { Button } from '@/kit/Button';
import { ConfirmPopup, type ConfirmPopupData } from '@/kit/ConfirmPopup';
import { DangerZone } from '@/kit/DangerZone';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Toggle } from '@/kit/Toggle';
import { Tooltip } from '@/kit/Tooltip';

const MAX_LOGO_BYTES = 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg'];

const TOOLTIP =
  'Please click below check box to enable/disable your company logo in all the reports for accounts production module.\n' +
  '                    Upload the logo that prints on the reports below; it must be a PNG or JPG of 1 MB or less.';

/** Whether the firm's logo prints on accounts production reports, and the logo itself. */
export function Logoonreport() {
  const [isEnabled, setIsEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [logo, setLogo] = useState<ReportLogoInfo | null>(null);
  /** Data URL of the uploaded logo. */
  const [preview, setPreview] = useState<string | null>(null);
  const isEnabledRef = useRef(true);
  const fileInput = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);

  const updateEnabled = (value: boolean) => {
    isEnabledRef.current = value;
    setIsEnabled(value);
  };

  const loadPreview = () => {
    firmSettingsService.getReportLogoImage().then(
      (blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (mounted.current) setPreview(typeof reader.result === 'string' ? reader.result : null);
        };
        reader.readAsDataURL(blob);
      },
      () => {
        if (mounted.current) setPreview(null);
      },
    );
  };

  const apply = (info: ReportLogoInfo) => {
    setLogo(info);
    updateEnabled(info.isEnabled);
    if (info.hasLogo) {
      loadPreview();
    } else {
      setPreview(null);
    }
  };

  useEffect(() => {
    mounted.current = true;
    firmSettingsService.getReportLogo().then(
      (info) => {
        if (mounted.current) apply(info);
      },
      (error) => notifyError(error),
    );
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggle = (enabled: boolean) => {
    const previous = isEnabledRef.current;
    updateEnabled(enabled);
    setIsSaving(true);
    firmSettingsService.setReportLogoEnabled(enabled).then(
      (info) => {
        setIsSaving(false);
        if (mounted.current) apply(info);
        notifySuccess(info.isEnabled ? 'Report logo enabled' : 'Report logo disabled');
      },
      (error) => {
        setIsSaving(false);
        if (mounted.current) updateEnabled(previous);
        notifyError(error);
      },
    );
  };

  /** PNG or JPG, 1 MB or less; the same limits the API enforces. */
  const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    input.value = '';
    if (!file) {
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      notifyError(NotificationTextMessage.fileJpgPng);
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      notifyError('File should be less than 1 MB');
      return;
    }

    setIsSaving(true);
    firmSettingsService.uploadReportLogo(file).then(
      (info) => {
        setIsSaving(false);
        if (mounted.current) apply(info);
        notifySuccess('Report logo uploaded');
      },
      (error) => {
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  /** Asks first: deleting the logo removes it from every report until a new one is uploaded. */
  const onDelete = () => {
    void openDialog<ConfirmPopupData, boolean>(ConfirmPopup, {
      data: {
        title: 'Delete report logo',
        message:
          'Delete the report logo? Reports will print without a logo until you upload a new one. This cannot be undone.',
      },
    })
      .afterClosed()
      .then((confirmed) => {
        if (confirmed === true) deleteLogo();
      });
  };

  const deleteLogo = () => {
    setIsSaving(true);
    firmSettingsService.deleteReportLogo().then(
      (info) => {
        setIsSaving(false);
        if (mounted.current) apply(info);
        notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
      },
      (error) => {
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  return (
    <div className="form wp-100">
      {/* 4 Nov: title hidden - the Settings menu this page sits in already shows "Logo on Report" highlighted. */}
      <PageHeader title="Logo on Report" hideTitle />
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <div className="d-flex align-items-center gap-10">
              <Toggle disabled={isSaving} checked={isEnabled} onCheckedChange={onToggle}>
                Enable /Disable Report Logo
              </Toggle>
            </div>
          </div>

          <div className="content-header-right mb-1">
            <div>
              <Tooltip title={TOOLTIP}>
                <Icon name="emoji_objects" className="text-yellow-200 highlight-icon" />
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="content-header d-block mt-1">
          <div className="form-field d-block">
            <p className="mb-1">Firm logo</p>

            <div className="d-flex align-items-center gap-15 mb-1">
              {preview && (
                <div className="logo-preview">
                  <img src={preview} alt="Report logo" className="wp-100" style={{ maxHeight: 120 }} />
                </div>
              )}
              {!preview && <div className="info-message">No logo has been uploaded. Reports print without a logo.</div>}
            </div>

            <div className="d-flex align-items-center gap-10">
              <input
                type="file"
                ref={fileInput}
                id="reportLogoFile"
                className="d-none"
                accept="image/png,image/jpeg"
                onChange={onFileSelected}
              />
              <Button
                className="action-button primary-button"
                disabled={isSaving}
                onClick={() => fileInput.current?.click()}
              >
                <Icon name="upload" />
                <span>{logo?.hasLogo ? 'Replace Logo' : 'Upload Logo'}</span>
              </Button>
            </div>

            {logo?.hasLogo && (
              <p className="font-size-14 mt-10 mb-0">
                {logo?.fileName} ({logo?.sizeBytes} bytes)
              </p>
            )}
            <p className="font-size-14 mt-10 mb-0">PNG or JPG, 1 MB or less.</p>
          </div>
        </div>

        {/* Design review 2 Nov 2026: a destructive action sits in a Danger zone. Deleting asks for confirmation first. */}
        {logo?.hasLogo && (
          <DangerZone
            items={[
              {
                id: 'delete-logo',
                title: 'Delete the report logo',
                description: 'Removes the firm logo. Reports print without a logo until you upload a new one.',
                actionLabel: 'Delete Logo',
                onAction: onDelete,
                disabled: isSaving,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
